import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  delay,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { AccessToken, DB_User } from '../interfaces/user';
import { WebAddresses } from '../app.config';
import { User } from '../helpers/user';
import { ApiResponse } from '../interfaces/api-response';
import { openSnackBar } from '../helpers/genericFunctions';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
  ) {}
  private authServerUrl = WebAddresses.TicketAuthorisation;
  //user: User = new User();
  access!: AccessToken;

  // create user
  create(user: DB_User): Observable<any> {
    return this.http.post(`${this.authServerUrl}/createUser`, user);
  }

  // login to user
  login(user: DB_User): Observable<any> {

    return this.http
      .post<ApiResponse>(`${this.authServerUrl}/login`, user)
      .pipe(
        tap((response) => {
          if (response.status != "success") {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        map((response) => response.data ?? []),
        catchError((error) => {
          console.error('Error logging in:', error);
          return throwError(() => new Error('Failed to log in'));
        }),
      );
  }

  /**
   * Complete registration from an invite link.
   * Called from the RegisterUserComponent after the user fills in
   * their name and password. The invite token carries the email,
   * org and permissions — no auth header needed.
   *
   * @param token    - JWT invite token from the URL query param
   * @param usr_NAME - Chosen username
   * @param usr_PASSWORD - Chosen password
   */
  completeRegistration(
    token: string,
    usr_NAME: string,
    usr_PASSWORD: string,
  ): Observable<any> {
    return this.http
      .post<ApiResponse>(`${this.authServerUrl}/register/complete`, {
        token,
        usr_NAME,
        usr_PASSWORD,
      })
      .pipe(
        tap((response) => {
          if (response.status != "success" && response.status != "success") {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        catchError((error) => {
          console.error('Error completing registration:', error);
          return throwError(() => error);
        }),
      );
  }

  refresh(organization?: string): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    let payload;
    if (organization) {
      payload = {
        refreshToken: refreshToken,
        org_name: organization,
      };
    } else {
      payload = {
        refreshToken: refreshToken,
      };
    }

    return this.http
      .post<ApiResponse>(`${this.authServerUrl}/refresh`, payload)
      .pipe(
        tap((response) => {
          console.log(response);
        }),
      );
  }

  /**
   * Send an invitation to a new user for the admin's current organization context.
   *
   * Creates a pending user on the backend and emails an activation link
   * valid for 48 hours. The link also comes back in the response so the
   * admin can share it manually.
   *
   * @param payload.email          - Email address of the person being invited
   * @param payload.permission_ids - Array of masterPermission PKs to assign on registration
   * @returns Observable with the API response containing the invite link
   */
  invite(payload: {
    email: string;
    permission_ids: number[];
  }): Observable<any> {
    return this.http
      .post<ApiResponse>(`${this.authServerUrl}/invite`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      })
      .pipe(
        tap((response) => {
          if (response.status != "success" && response.status != "success") {
            openSnackBar(response.message, 10000, this.snackBar);
          }
        }),
        catchError((error) => {
          console.error('Error sending invite:', error);
          return throwError(() => error);
        }),
      );
  }

  // logout of user
  logout(): Observable<any> {
    let token = localStorage.getItem('token');
    return this.http.post(`${this.authServerUrl}/logout`, token);
  }
}
